import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import cls from 'clsx';
import { CurrencyCurrency } from '@linktivity/ars-neo';
import {
  JAPANESE_LOCALE,
  CHINESE_LOCALE,
  TRADITIONAL_CHINESE_LOCALE
} from '@link/share/constants';
import { formatPrice, getCurrencyDecimals } from '@link/share/utils';
import { PriceProps } from './Price.types';
import styles from './price.module.css';

const formatAmount = (amount: number, currency: string): string => {
  const decimals = getCurrencyDecimals(currency);
  return formatPrice(
    decimals === 0 ? Math.round(amount) : amount.toFixed(decimals)
  );
};

const Price: React.FC<PriceProps> = ({
  amount,
  currencyCode = CurrencyCurrency.Jpy,
  originalAmount,
  originalCurrencyCode,
  className = '',
  needApproximate = false,
  formatOptions = {
    smallCurrencyCode: false
  },
  color,
  secondaryAmount
}) => {
  const { t, i18n } = useTranslation();

  const primaryCurrency = (
    currencyCode === CurrencyCurrency.Unknown
      ? CurrencyCurrency.Jpy
      : currencyCode
  ) as string;
  const finalOriginalAmount = originalAmount ?? secondaryAmount;
  const finalOriginalCurrency =
    (originalCurrencyCode as string) ?? primaryCurrency;

  const showDual =
    finalOriginalAmount !== undefined &&
    finalOriginalAmount !== null &&
    finalOriginalCurrency !== primaryCurrency;

  const usesCurrencySuffix = (currency: string) =>
    (currency === CurrencyCurrency.Jpy && i18n.language === JAPANESE_LOCALE) ||
    (currency === CurrencyCurrency.Cny &&
      (i18n.language === CHINESE_LOCALE ||
        i18n.language === TRADITIONAL_CHINESE_LOCALE));

  const renderSinglePrice = (val: number, cur: string, isPrimary: boolean) => {
    const formattedAmt = formatAmount(val, cur);

    // Determine format key and currency label
    let i18nKey = 'share.price.prefix'; // Default: USD100 (Prefix)
    let currencyLabel = cur;

    const currencyUsesSuffix = usesCurrencySuffix(cur);

    if (currencyUsesSuffix) {
      i18nKey = 'share.price.suffix';
      currencyLabel = t(
        cur === CurrencyCurrency.Jpy
          ? 'share.price.jpyWord'
          : 'share.price.cnyWord'
      );
    }

    return (
      <span className={styles.priceValue}>
        <Trans
          i18nKey={i18nKey}
          values={{
            amount: formattedAmt,
            currency: currencyLabel
          }}
          components={{
            amount: <span className={styles.amount} data-stt-ignore />,
            currency: (
              <span
                data-stt-ignore
                className={cls(
                  styles.currency,
                  isPrimary &&
                    formatOptions.smallCurrencyCode &&
                    styles.smallCurrencyCode
                )}
              />
            )
          }}
        />
      </span>
    );
  };

  const renderPrice = () => {
    const primaryColorStyle = color ? { color } : undefined;
    const alignApproximateWithSuffix =
      formatOptions.smallCurrencyCode && usesCurrencySuffix(primaryCurrency);

    return (
      <>
        <div className={cls(styles.primaryPrice)} style={primaryColorStyle}>
          {renderSinglePrice(amount, primaryCurrency, true)}
          {needApproximate && (
            <span
              data-stt-ignore
              className={cls(
                alignApproximateWithSuffix && styles.suffixApproximate
              )}
            >
              {t('share.common.tilde')}
            </span>
          )}
        </div>
        {showDual && (
          <div className={styles.secondaryPrice}>
            {renderSinglePrice(
              finalOriginalAmount!,
              finalOriginalCurrency,
              false
            )}
          </div>
        )}
      </>
    );
  };

  return <div className={cls(styles.price, className)}>{renderPrice()}</div>;
};

export default Price;
